import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Permissions } from '@app/core/guard/Permissions';
import { AnnualCostByScenarios } from '@app/core/models/demand/demand-request';
import { OperationAction } from '@app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-management/ndf-management-item/ndf-management-item.component';
import { DemandOutput } from '@app/features/dashboard/control/derivative-management/derivatives-old-version/ndf-management/ndf-management.types';
import { VaultBaseComponent } from '@app/shared/components/base/vault-base.component';
import {
  NdfDemandsCalculationsFiltered,
  NdfDemandsCalculationsOperation,
} from '@interfaces/a-organizar/ndfDemandsCalculationsFiltered';
import { NdfOperationDates } from '@interfaces/a-organizar/ndfOperationDates';
import { Option } from '@interfaces/common/option';
import { TranslateService } from '@ngx-translate/core';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { UtilsService } from '@services/helpers/utils.service';
import { FinancialInstitutionService } from '@services/http/financial-institution.service';
import { InputNumberInputEvent } from 'primeng/inputnumber';
import { Subject, Subscription, debounceTime } from 'rxjs';

export interface AsideLabel {
  label: string;
  highlight: boolean;
}

@Component({
  selector: 'app-ndf-management-grid',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  templateUrl: './ndf-management-grid.component.html',
  styleUrls: ['./ndf-management-grid.component.scss'],
})
export class NdfManagementGridComponent
  extends VaultBaseComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('content', { static: true }) content!: ElementRef;
  @Input() demand: NdfDemandsCalculationsFiltered | null = null;
  @Input() disableFees: boolean = false;
  @Input() operations: NdfDemandsCalculationsOperation[] = [];
  @Output() sendAnnualCostByScenarios: EventEmitter<AnnualCostByScenarios[]> = new EventEmitter<
    AnnualCostByScenarios[]
  >();
  @Output()
  sendUpdatedDemand: EventEmitter<DemandOutput | null> = new EventEmitter<DemandOutput | null>();
  @Output() sendOperationAction: EventEmitter<OperationAction> =
    new EventEmitter<OperationAction>();
  @Output() sendCalculateDueDate: EventEmitter<NdfOperationDates> =
    new EventEmitter<NdfOperationDates>();

  annualCostByScenarios: AnnualCostByScenarios[] = [];
  hasSimulation: boolean = false;
  demandPermissions = Permissions.DerivativeCalculator;
  currencyCodeStr: string = '';

  asideLabels: AsideLabel[] = [];

  financialInstitutionsOptions: Option[] = [];
  selectedFinancialInstitution: Option | null = null;

  formFee = this.formBuilder.group({
    fees: this.formBuilder.array([]),
  });

  private inputSubject = new Subject<{
    event: InputNumberInputEvent;
    index: number;
  }>();
  private subscription = new Subscription();

  constructor(
    translateService: TranslateService,
    private financialInstitutionService: FinancialInstitutionService,
    private toast: ToastHelperService,
    private formBuilder: FormBuilder,
    private utilsService: UtilsService
  ) {
    super(translateService);
  }

  initializeAsideLabels() {
    this.asideLabels = [
      {
        label: this.t(this.tk.Management.Modality),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfBank),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfDateOfOperation),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfDueDate),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfDeadlineDays),
        highlight: false,
      },
      {
        label: 'Parcelas',
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfNotional) + ' (' + this.currencyCodeStr + ')',
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfForward),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfCap),
        highlight: true,
      },
      {
        label: this.t(this.tk.Management.NdfReferenceExchange),
        highlight: true,
      },
      // Campos específicos para Call Spread e Call vanilla
      {
        label: 'Strike compra',
        highlight: false,
      },
      {
        label: 'Strike venda',
        highlight: false,
      },
      {
        label: 'Prêmio BRL',
        highlight: false,
      },
      // Campos para SWAP
      // {
      //   label: 'Número de parcelas',
      //   highlight: false,
      // },
      {
        label: 'Indexador ativo',
        highlight: false,
      },
      {
        label: 'Indexador passivo',
        highlight: false,
      },
      {
        label: 'Taxa fixa ativo a.a.%',
        highlight: false,
      },
      {
        label: 'Taxa fixa passivo a.a.%',
        highlight: false,
      },
      // {
      //   label: 'Fluxo de juros',
      //   highlight: false,
      // },
      // {
      //   label: 'Fluxo de amortização',
      //   highlight: false,
      // },
      // {
      //   label: 'Número de amortizações',
      //   highlight: false,
      // },
      {
        label: this.t(this.tk.General.Observations),
        highlight: false,
      },

      {
        label: '',
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfVolumeProtectedUpToCap),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfLastDatePtaxPassedCap),
        highlight: false,
      },
      {
        label: '',
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfNotionalBrlByReference),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfNominalCostBrl),
        highlight: false,
      },
      {
        label:
          this.t(this.tk.Management.NdfNominalCost) +
          ' ' +
          this.t(this.tk.Management.NdfNotionalFlat),
        highlight: false,
      },
      {
        label:
          this.t(this.tk.Management.NdfNominalCost) +
          ' ' +
          this.t(this.tk.Management.NdfNotionalAa),
        highlight: false,
      },
      // No array asideLabels, adicione os novos campos no lugar apropriado
      // {
      //   label: 'Fluxo de juros',
      //   highlight: false,
      // },
      // {
      //   label: 'Fluxo de amortização',
      //   highlight: false,
      // },
      // Adicione após o label "Prêmio"
      {
        label: 'Prêmio %',
        highlight: false,
      },
      {
        label: 'Prêmio % a.a.',
        highlight: false,
      },
      {
        label: 'Proteção cambial máxima %',
        highlight: false,
      },
      // {
      //   label: 'Variação acima limitador %',
      //   highlight: false,
      // },
      // {
      //   label: 'Variação acima limitador R$',
      //   highlight: false,
      // },
      {
        label: '',
        highlight: true,
      },
      // Campos de liquidação
      {
        label: this.t(this.tk.Management.NdfSettlementDate),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfSettlementNotional),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfSettlementRate),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfSettlementSpot),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfGrossAdjustment),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfWithholdingTax),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfIncomeTaxPayable),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfNetAdjustment),
        highlight: false,
      },
      {
        label: this.t(this.tk.Management.NdfSettlementScenarios),
        highlight: false,
      },
    ];
  }

  ngOnInit(): void {
    this.currencyCodeStr = this.demand?.internationalCurrency?.currencyCode || '';
    this.initializeAsideLabels();
    console.log(this.currencyCodeStr);
    this.subscription = this.inputSubject
      .pipe(debounceTime(500))
      .subscribe(({ event, index }) => this.onInputFee(event, index));
    this.getFinancialInstitutions();

    // Inicia o tour ao carregar o componente

    /* if (!this.demand?.id && (this.demand?.operations || [])?.length > 0) {
      const forwardRate = this.demand?.operations[0].forwardRate || 0;
      this.fillDefaultFeesBasedOnForwardRate(forwardRate);
      return;
    }*/

    if (this.demand?.id && (this.demand?.operations || [])?.length > 0) {
      const firstOperation = this.demand.operations.find(
        operation => operation?.annualCostByScenarios?.length
      );

      firstOperation?.annualCostByScenarios?.forEach(scenario => {
        const item = this.formBuilder.group({
          name: [scenario?.name],
          scenarioValue: [scenario?.scenarioValue],
        });

        this.feesArray.push(item);

        this.annualCostByScenarios.push({
          name: scenario?.name,
          scenarioValue: scenario?.scenarioValue,
        } as AnnualCostByScenarios);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disableFees']) {
      this.toggleFormControls(this.disableFees);
    }
  }

  override ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.inputSubject.unsubscribe();
  }

  fillDefaultFeesBasedOnForwardRate(forwardRate: number = 0): void {
    const scenarios = [
      forwardRate - 0.1,
      forwardRate - 0.05,
      forwardRate,
      forwardRate + 0.05,
      forwardRate + 0.1,
    ];

    this.feesArray.clear();
    this.annualCostByScenarios = [];
    scenarios.forEach((scenario, index) => {
      const item = this.formBuilder.group({
        name: [this.t(this.tk.Management.NdfScenario) + ` ${index + 1}`],
        scenarioValue: [scenario],
      });

      if (scenario >= 0) {
        this.feesArray.push(item);
        this.annualCostByScenarios.push({
          name: this.t(this.tk.Management.NdfScenario) + ` ${index + 1}`,
          scenarioValue: scenario,
        } as AnnualCostByScenarios);
      }
    });

    //this.sendAnnualCostByScenarios.emit(this.annualCostByScenarios);
  }

  toggleFormControls(isDisabled: boolean): void {
    this.feesArray.controls.forEach(control => {
      if (isDisabled) {
        control.disable();
      } else {
        control.enable();
      }
    });
  }

  getSelectedFinancialInstitution(index: number): Option {
    return {
      label: this.financialInstitutionsOptions[index].label,
      value: this.financialInstitutionsOptions[index].value,
    };
  }

  getFinancialInstitutions() {
    this.utilsService.setIsLoading(true);
    this.financialInstitutionService.getFinancialInstitutions({ PageSize: 500 }).subscribe({
      next: response => {
        this.utilsService.setIsLoading(false);
        if (response) {
          this.financialInstitutionsOptions = (response?.data || []).map(item => {
            return {
              label: `${item.displayName} | ${item.compensationCode}`,
              value: item.id,
              data: item,
            };
          });
        }
      },
      error: error => {
        this.utilsService.setIsLoading(false);
        console.error(error);

        this.toast.showToastError(
          this.t(this.tk.Management.ErrorLoadingFinancialInstitutions),
          error?.errors?.length ? error.errors[0].message : this.t(this.tk.General.RequestError)
        );
      },
    });
  }

  addFee(): void {
    this.addFeeControl();
  }
  sortDemandByOrder(demand: NdfDemandsCalculationsFiltered): NdfDemandsCalculationsFiltered {
    if (!demand?.operations) return demand;

    const sortedOperations = [...demand.operations].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    return {
      ...demand,
      operations: sortedOperations,
    } as NdfDemandsCalculationsFiltered;
  }
  handleDemandChange(output: DemandOutput | null): void {
    const demand = output?.demand;
    if (!demand) {
      return;
    }
    this.demand = this.sortDemandByOrder({
      ...demand,
      annualCostByScenarios: this.annualCostByScenarios,
    });

    if (this.demand.operations.length === 1) {
      const firstOperation = this.demand.operations[0];
      firstOperation.annualCostByScenarios = [];
      if (firstOperation.forwardRate > 0) {
        this.fillDefaultFeesBasedOnForwardRate(firstOperation.forwardRate);
      }
    }

    this.sendUpdatedDemand.emit({
      demand: this.demand,
      postSimulation: output.postSimulation,
    });
  }

  sortDemandBySequentialId(demand: NdfDemandsCalculationsFiltered): NdfDemandsCalculationsFiltered {
    if (demand) {
      demand.operations = demand.operations.sort((a, b) => {
        const idA = a?.sequentialId?.replace('DERIV', '') || '0';
        const idB = b?.sequentialId?.replace('DERIV', '') || '0';

        return Number(idA) - Number(idB);
      });
    }
    return demand;
  }

  onInputFee(event: InputNumberInputEvent, index: number) {
    const value = event.value || 0;

    const annualCostByScenario = {
      ...this.annualCostByScenarios[index],
      scenarioValue: parseFloat(value.toString()),
      name: this.t(this.tk.Management.NdfScenario) + ' ' + (index + 1),
    };

    this.annualCostByScenarios[index] = annualCostByScenario;

    this.sendAnnualCostByScenarios.emit(this.annualCostByScenarios);
  }

  getLetterFromIndex(index: number): string {
    let letters = '';
    while (index >= 0) {
      letters = String.fromCharCode((index % 26) + 65) + letters;
      index = Math.floor(index / 26) - 1;
    }
    return letters;
  }

  onInputFeeDebounced(event: InputNumberInputEvent, index: number): void {
    this.inputSubject.next({ event, index });
  }

  getFeeFormGroup(index: number): FormGroup {
    return this.feesArray.at(index) as FormGroup;
  }

  handleFeeArray(event: InputNumberInputEvent, index: number): void {
    const control = this.feesArray.at(index);
    const value = event.value;
    control?.setValue(value);
  }

  get feesArray(): FormArray {
    return this.formFee.get('fees') as FormArray;
  }

  addFeeControl(): void {
    const item = this.formBuilder.group({
      name: [''],
      scenarioValue: [0],
    });

    this.feesArray.push(item);

    this.annualCostByScenarios.push({
      name: '',
      scenarioValue: 0,
    } as AnnualCostByScenarios);
  }

  getFeeFormControl(index: number): FormControl {
    return this.feesArray.at(index) as FormControl;
  }

  removeFeeControl(index: number): void {
    this.feesArray.removeAt(index);
    this.annualCostByScenarios.splice(index, 1);
  }

  handleOperationAction(operationAction: OperationAction): void {
    this.sendOperationAction.emit(operationAction);
  }
}
